import type { RosterStats } from "../types";
import type { SeatUsage } from "../../organizations/types";
import { Users, UserCheck, Clock, UserX, Gauge } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface RosterStatsBarProps {
  stats: RosterStats | undefined;
  seatUsage?: SeatUsage | null;
  isLoading: boolean;
}

function StatCard({
  label,
  value,
  icon: Icon,
  colorClass,
}: {
  label: string;
  value: number | undefined;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <div className={`rounded-md p-2 ${colorClass}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        {value !== undefined ? (
          <p className="text-xl font-semibold text-foreground">{value}</p>
        ) : (
          <div className="h-7 w-12 animate-pulse rounded bg-muted" />
        )}
      </div>
    </div>
  );
}

const SEAT_STATUS_STYLES: Record<string, { color: string; bg: string; barColor: string }> = {
  OK: { color: "text-success", bg: "bg-success/10", barColor: "bg-success" },
  WARNING: { color: "text-amber-500", bg: "bg-amber-500/10", barColor: "bg-amber-500" },
  CRITICAL: { color: "text-destructive", bg: "bg-destructive/10", barColor: "bg-destructive" },
  FULL: { color: "text-destructive", bg: "bg-destructive/10", barColor: "bg-destructive" },
  UNLIMITED: { color: "text-primary", bg: "bg-primary/10", barColor: "bg-primary" },
};

function SeatCapacityCard({ seatUsage }: { seatUsage: SeatUsage }) {
  const { t } = useLocale();
  const style = SEAT_STATUS_STYLES[seatUsage.status] ?? SEAT_STATUS_STYLES.OK;
  const isUnlimited = seatUsage.status === "UNLIMITED";
  const label = isUnlimited
    ? t("seats.unlimited")
    : `${seatUsage.used} / ${seatUsage.limit}`;
  const percentage = seatUsage.percentage ?? 0;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <div className={`rounded-md p-2 ${style.bg} ${style.color}`}>
        <Gauge className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground">{t("seats.seatCapacity")}</p>
        <p className={`text-xl font-semibold ${style.color}`}>{label}</p>
        {!isUnlimited && (
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-all ${style.barColor}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function RosterStatsBar({ stats, seatUsage, isLoading }: RosterStatsBarProps) {
  const { t } = useLocale();
  const showSeatCard = seatUsage != null;
  const gridCols = showSeatCard ? "lg:grid-cols-5" : "lg:grid-cols-4";

  if (isLoading && !stats) {
    return (
      <div className={`grid grid-cols-2 gap-3 ${gridCols}`}>
        {Array.from({ length: showSeatCard ? 5 : 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[72px] animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 gap-3 ${gridCols}`}>
      <StatCard
        label={t("roster.statTotalEntries")}
        value={stats?.totalEntries}
        icon={Users}
        colorClass="bg-primary/10 text-primary"
      />
      <StatCard
        label={t("roster.statActiveUsers")}
        value={stats?.activeUsers}
        icon={UserCheck}
        colorClass="bg-success/10 text-success"
      />
      <StatCard
        label={t("roster.statusPending")}
        value={stats?.pendingInvites}
        icon={Clock}
        colorClass="bg-warning/10 text-warning"
      />
      <StatCard
        label={t("roster.statusNotInvited")}
        value={stats?.notInvited}
        icon={UserX}
        colorClass="bg-destructive/10 text-destructive"
      />
      {showSeatCard && seatUsage && (
        <SeatCapacityCard seatUsage={seatUsage} />
      )}
    </div>
  );
}
